import { useDispatch, useSelector } from "react-redux";
import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useSnackbar, withSnackbar } from "notistack";
import { Formik } from "formik";

import { ModalBody, ModalFooter } from "../../../../../application/modal";
import {
  BtnType, Button, Label, MultiSelect, TextBox,
} from "../../../../../modules/primitives";
import { useModelPopup } from "../../../../../common/hooks";
import { httpPut } from "../../../../../common/httpCall";
import { updateProjectFieldGroups } from "modules/projects/project/project.action";
import { getPlantsAndAreas } from "modules/admin/adminPlantsAndAreas/adminPlantsAndAreas.actions";
import PlantMultiSelect from "../../../../../common/components/plantMultiselect";
import LoadingComponent from "modules/primitives/loading/loading.component";
import { makeFormikObj } from "../../../../../common/helper";

const ProjectFieldsEdit = ({ selectedGroup, taskInstance, handleSave }) => {
  const popup = useModelPopup();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const formRef = useRef();

  const {
    adminPlantsAndAreasReducer: { plantsAndAreas },
    councilReducer: { selectedCouncil },
    projectsReducer: {
      projectReducer: { selectedProject },
    },
  } = useSelector((state) => state);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [data, setData] = useState(null);
  const [model, setModel] = useState({});

  useEffect(() => {
    if (!plantsAndAreas.length && selectedCouncil?.name === 'Ford') {
      dispatch(
        getPlantsAndAreas({
          enqueueSnackbar,
        }),
      );
    }
  }, [plantsAndAreas, selectedCouncil]);

  useEffect(() => {
    if (selectedGroup) {
      const makeData = {
        ...selectedGroup,
        project_fields: selectedGroup.project_fields.map(makeFormikObj),
      }

      setData(makeData);

      const fields = makeData.project_fields
        .reduce((acc, elem) => ({ ...acc, [elem.project_field_template_id || elem.id]: elem.values }), {});
      const possiblePlants = getPossiblePlantsFromSource(selectedProject);

      setModel({
        ...fields,
        selectedPlant1: possiblePlants,
        selectedAreas1: getPossibleAreasFromSource(data, possiblePlants),
      });
    }
  }, [selectedGroup]);

  const getPlantsAsOptions = useMemo(() => {
    return plantsAndAreas.map(plant => ({
      id: plant.id,
      name: plant.attributes.name,
      areas: plant.attributes.areas,
    }));
  }, [plantsAndAreas]);

  const getPossiblePlantsFromSource = useCallback((data) => {
    if (!data?.possible_plants.length) {
      return [];
    }

    return plantsAndAreas
      .map(p => ({...p, ...p.attributes}))
      .filter(p => data?.possible_plants.some(plant => plant.id === p.id));
  }, [plantsAndAreas]);

  const getPossibleAreasFromSource = useCallback((data, possiblePlants) => {
    if (!data?.possible_areas?.length) {
      return []
    }

    const flattenAreas = possiblePlants.reduce((acc, plant) => [...acc, ...plant.areas], []);

    return data?.possible_areas?.map(a => flattenAreas.find(a1 => a1.area_id === a.area_id));
  }, [plantsAndAreas]);

  const onSubmitForm = values => {
    setIsSubmitting(true);

    const isFord = selectedCouncil?.name === 'Ford';
    const groupHasPlant = selectedGroup?.project_fields?.some(f => f.field_name === 'Possible Replications (Plants)');
    let fordProps = {};

    if (isFord && groupHasPlant) {
      fordProps = {
        possible_plant_ids: values.selectedPlant1?.map(p => p.id) || [],
        possible_area_ids: values.selectedAreas1?.map(a => a.area_id) || [],
      }
    }

    const data = {
      ...fordProps,
      project_field_group_id: selectedGroup?.id,
      project_field_template_values: Object.keys(values)
        .filter(k => {
          const getFieldDetails = selectedGroup.project_fields.find(elem => (elem.project_field_template_id || elem.id) === k);
          return getFieldDetails?.field_name !== 'Possible Replications (Plants)' &&
            getFieldDetails?.field_name !== 'Possible Replications (Areas)' &&
            k !== 'selectedPlant1' &&
            k !== 'selectedAreas1';
        })
        .map(k => {
          const getFieldDetails = selectedGroup.project_fields.find(elem => (elem.project_field_template_id || elem.id) === k);
          let otherProps;
          let payload = {};

          if (getFieldDetails.project_field_template_id) {
            otherProps = {
              project_field_template_id: getFieldDetails.project_field_template_id
            }
          } else if (getFieldDetails.id) {
            otherProps = {
              id: getFieldDetails.id
            }
          }

          if (getFieldDetails.field_type === 'text') {
            payload = {
              ...payload,
              ...otherProps,
              field_value: String(values[k] || '').length ? values[k] : null,
            }
          }

          if (getFieldDetails.field_type === 'dropdown') {
            payload = {
              ...payload,
              ...otherProps,
              option_selected: values[k].map(el => el.id),
            }
          }

          return payload;
        }),
    };
    console.log('data', data);

    if (taskInstance) {
      handleSave(data);
      popup.hide();
      return;
    }

    httpPut({
      call: `projects/${selectedProject.id}/project_fields`,
      data,
    })
      .pipe()
      .subscribe((res) => {
        setIsSubmitting(false);
        if (res.response) {
          const message = selectedGroup.name
            ? `Successfully updated ${selectedGroup.name} group fields`
            : 'Successfully updated fields';

          enqueueSnackbar(message, {
            variant: "success",
          });

          dispatch(updateProjectFieldGroups({
            response: res.response,
            group: selectedGroup,
          }));

          popup.hide();
        }
      });
  };

  const handleCancelClick = () => {
    popup.hide();
  };

  const handleSubmit = () => {
    const submitForm = formRef?.current?.submitForm;
    submitForm();
  }

  if (!data) {
    return <LoadingComponent customText="Loading group fields..." />
  }

  return (
    <div>
      <ModalBody>
        <div>
          <div className="d-flex flex-column">
            <Formik
              innerRef={formRef}
              enableReinitialize
              initialValues={model}
              onSubmit={(values) => {
                onSubmitForm(values, data);
              }}
            >
              {({ handleSubmit, setFieldValue, values, ...formikprops }) => (
                <form onSubmit={handleSubmit}>
                  <h5 className="font-weight-bold mb-4">{selectedGroup.name || 'Ungrouped fields'}</h5>
                  {
                    data.project_fields.map(field => {
                      if (field.field_name === 'Possible Replications (Plants)' && selectedCouncil?.name === 'Ford') {
                        return (
                          <div key={field.project_field_template_id || field.id} className="mb-3">
                            <PlantMultiSelect
                              formikprops={{values, setFieldValue, ...formikprops}}
                              plants={getPlantsAsOptions}
                            />
                          </div>
                        )
                      }

                      if (field.field_name === 'Possible Replications (Areas)' && selectedCouncil?.name === 'Ford') {
                        return null;
                      }

                      return (
                        <div key={field.project_field_template_id || field.id}>
                          {
                            field.field_type === 'text' ? (
                              <div>
                                <Label>{field.field_name}</Label>
                                {
                                  field.big_box_text ? (
                                    <textarea
                                      className="w-100 border small p-2"
                                      value={values[field.project_field_template_id || field.id]}
                                      placeholder={field.field_placeholder}
                                      onChange={(evt) => setFieldValue(field.project_field_template_id || field.id, evt.target.value)}
                                      rows="5"
                                    ></textarea>
                                  ) : (
                                    <TextBox
                                      name={field.project_field_template_id || field.id}
                                      placeholder={field.field_placeholder}
                                      formProps={{ ...formikprops, values }}
                                      className="w-100"
                                      value={values[field.project_field_template_id || field.id]}
                                    />
                                  )
                                }
                              </div>
                            ) : null
                          }
                          {
                            field.field_type === 'dropdown' ? (
                              <div>
                                <Label>{field.field_name}</Label>
                                <MultiSelect
                                  name={field.project_field_template_id || field.id}
                                  values={values[field.project_field_template_id || field.id]}
                                  labelField="name"
                                  valueField="id"
                                  onChange={val => setFieldValue(field.project_field_template_id || field.id, val)}
                                  options={field.option_values}
                                  multi={field.multiselect}
                                  placeholder={field.field_placeholder}
                                />
                              </div>
                            ) : null
                          }
                        </div>
                      )
                    })
                  }
                </form>
              )}
            </Formik>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        {
          isSubmitting ? (
            <LoadingComponent customText="Saving data..." />
          ) : (
            <div className="d-flex justify-content-end">
              <Button btn={BtnType.FRAME_LESS} onClick={handleCancelClick}>
                Cancel
              </Button>
              <Button type="submit" btn={BtnType.REGULAR} onClick={handleSubmit}>
                Submit
              </Button>
            </div>
          )
        }
      </ModalFooter>
    </div>
  );
};
export default React.memo(withSnackbar(ProjectFieldsEdit));
